import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import {
  CreateLocation,
  Location,
  LocationListRequest,
  PaginatedLocationsResponse,
  PaginationMeta,
} from '../types/location'
import {
  loadLocationList,
  loadLocations,
  modifyLocation,
  removeLocation,
  saveLocation,
} from '../api/location'

const emptyPagination: PaginationMeta = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 0,
}

// get all locations
export const getLocations = createAsyncThunk(
  'location/getLocations',
  async (_, thunkApi) => {
    try {
      return await loadLocations()
    } catch (e) {
      return thunkApi.rejectWithValue('getLocationError')
    }
  },
)

export const getLocationList = createAsyncThunk(
  'location/getLocationList',
  async (request: LocationListRequest, thunkApi) => {
    try {
      return await loadLocationList(request)
    } catch (e) {
      return thunkApi.rejectWithValue('getLocationListError')
    }
  },
)

// add location
export const addLocation = createAsyncThunk(
  'location/addLocation',
  async (location: CreateLocation, thunkApi) => {
    try {
      return await saveLocation(location)
    } catch (e) {
      return thunkApi.rejectWithValue('addLocationError')
    }
  },
)

// update location
export const updateLocation = createAsyncThunk(
  'location/updateLocation',
  async (location: Location, thunkApi) => {
    try {
      return await modifyLocation(location)
    } catch (e) {
      return thunkApi.rejectWithValue('updateLocationError')
    }
  },
)

// delete objective
export const deleteLocation = createAsyncThunk(
  'location/deleteLocation',
  async (id: string, thunkApi) => {
    try {
      return await removeLocation(id)
    } catch (e) {
      return thunkApi.rejectWithValue('deleteLocationError')
    }
  },
)

interface LocationSliceState {
  locations: Location[]
  pagedLocations: Location[]
  locationLoading: boolean
  locationError?: SerializedError
  pagination: PaginationMeta
  activeListRequestId?: string
}

const initialState: LocationSliceState = {
  locations: [],
  pagedLocations: [],
  locationLoading: false,
  locationError: undefined,
  pagination: emptyPagination,
  activeListRequestId: undefined,
}

const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getLocations.fulfilled, (
      state,
      action: PayloadAction<Location[]>,
    ) => {
      state.locations = action.payload
      state.locationLoading = false
      state.locationError = undefined
    })
    builder.addCase(getLocations.pending, (state) => {
      state.locationLoading = true
      state.locationError = undefined
    })
    builder.addCase(getLocations.rejected, (state, action) => {
      state.locationLoading = false
      state.locationError = action.error
    })
    builder.addCase(getLocationList.fulfilled, (
      state,
      action: PayloadAction<PaginatedLocationsResponse> & { meta: any },
    ) => {
      if (state.activeListRequestId !== action.meta.requestId) {
        return
      }

      state.pagedLocations = action.payload.items
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
      }
      state.activeListRequestId = undefined
      state.locationLoading = false
      state.locationError = undefined
    })
    builder.addCase(getLocationList.pending, (state, action: any) => {
      state.activeListRequestId = action.meta.requestId
      state.pagedLocations = []
      state.locationLoading = true
      state.locationError = undefined
    })
    builder.addCase(getLocationList.rejected, (state, action: any) => {
      if (state.activeListRequestId !== action.meta.requestId) {
        return
      }

      state.activeListRequestId = undefined
      state.locationLoading = false
      state.locationError = action.error
    })
    builder.addCase(addLocation.fulfilled, (state, action: PayloadAction<Location>) => {
      state.locations = [action.payload, ...state.locations]

      if (state.pagination.page === 1) {
        state.pagedLocations = [action.payload, ...state.pagedLocations].slice(
          0,
          state.pagination.limit,
        )
        state.pagination.total += 1
        state.pagination.totalPages = Math.ceil(
          state.pagination.total / state.pagination.limit,
        )
      }

      state.locationLoading = false
      state.locationError = undefined
    })
    builder.addCase(addLocation.pending, (state) => {
      state.locationLoading = true
      state.locationError = undefined
    })
    builder.addCase(addLocation.rejected, (state, action) => {
      state.locationLoading = false
      state.locationError = action.error
    })
    builder.addCase(deleteLocation.fulfilled, (state, action: PayloadAction<string>) => {
      const wasOnCurrentPage = state.pagedLocations.some(
        (location) => location.id === action.payload,
      )

      state.locations = state.locations.filter(
        (location) => location.id !== action.payload,
      )
      state.pagedLocations = state.pagedLocations.filter(
        (location) => location.id !== action.payload,
      )

      if (wasOnCurrentPage && state.pagination.total > 0) {
        state.pagination.total -= 1
        state.pagination.totalPages =
          state.pagination.total === 0
            ? 0
            : Math.ceil(state.pagination.total / state.pagination.limit)
      }

      state.locationLoading = false
      state.locationError = undefined
    })
    builder.addCase(deleteLocation.pending, (state) => {
      state.locationLoading = true
      state.locationError = undefined
    })
    builder.addCase(deleteLocation.rejected, (state, action) => {
      state.locationLoading = false
      state.locationError = action.error
    })
    builder.addCase(updateLocation.fulfilled, (
      state,
      action: PayloadAction<Location>,
    ) => {
      if (state.locations.length > 0) {
        const updatedObjectives = state.locations.map((location) =>
          location.id === action.payload.id ? action.payload : location,
        )
        state.locations = updatedObjectives
      }

      state.pagedLocations = state.pagedLocations.map((location) =>
        location.id === action.payload.id ? action.payload : location,
      )

      state.locationLoading = false
      state.locationError = undefined
    })
    builder.addCase(updateLocation.pending, (state) => {
      state.locationLoading = true
      state.locationError = undefined
    })
    builder.addCase(updateLocation.rejected, (state, action) => {
      state.locationLoading = false
      state.locationError = action.error
    })
  },

  // (builder) => {
  //     builder.addCase(getDevelopers.fulfilled, (state, action) => {
  //         state.developers = action.payload as IDeveloper[];
  //     })

  //     builder.addCase(addDeveloper.fulfilled, (state, action) => {
  //         state.developers.push(action.payload as IDeveloper);
  //     })

  //     builder.addCase(deleteDeveloper.fulfilled, (state, action) => {
  //         state.developers = state.developers.filter(developer => developer.id != action.payload)
  //     })
  // }
})

export const {} = locationSlice.actions
export default locationSlice.reducer
